import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Upload, Eye, EyeOff } from "lucide-react";
import { api, mediaUrl, uploadFile, formatError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { inputCls, Field } from "@/admin/fields";

const CATEGORIES = ["Training", "Matches", "Tournaments", "Team Photos", "Club Events", "Special Moments"];

export default function GalleryManager() {
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploadingTo, setUploadingTo] = useState(null);
  const fileRef = useRef();

  const load = async () => { setLoading(true); const { data } = await api.get("/admin/albums"); setAlbums(data); setLoading(false); };
  useEffect(() => { load(); }, []);

  const blank = () => ({ title: "", category: "Training", description: "", published: false, consent_confirmed: false });
  const save = async () => {
    if (!editing.consent_confirmed && !editing.id) return toast.error("Please confirm image consent before creating an album.");
    setSaving(true);
    try {
      if (editing.id) await api.put(`/admin/albums/${editing.id}`, editing);
      else await api.post("/admin/albums", editing);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const removeAlbum = async (id) => { if (!window.confirm("Delete album and all its photos?")) return; await api.delete(`/admin/albums/${id}`); toast.success("Deleted"); load(); };
  const togglePublish = async (a) => { await api.put(`/admin/albums/${a.id}`, { published: !a.published }); load(); };

  const addPhotos = async (albumId, files) => {
    setUploadingTo(albumId);
    try {
      for (const file of files) {
        const res = await uploadFile(file);
        await api.post(`/admin/albums/${albumId}/photos`, { url: res.url, caption: "" });
      }
      toast.success("Photos uploaded"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setUploadingTo(null); }
  };
  const setCover = async (album, url) => { await api.put(`/admin/albums/${album.id}`, { cover_url: url }); toast.success("Cover set"); load(); };
  const deletePhoto = async (id) => { await api.delete(`/admin/photos/${id}`); load(); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold uppercase text-white">Gallery</h1><p className="text-sm text-slate-400">Organise albums and photos. Confirm consent before publishing children's photos.</p></div>
        <button onClick={() => setEditing(blank())} data-testid="album-add-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-4 py-2 text-sm font-bold uppercase text-[#0A1626]"><Plus className="h-4 w-4" /> New Album</button>
      </div>

      {loading ? <div className="py-20 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-slate-400" /></div> : (
        <div className="mt-6 space-y-6">
          {albums.map((a) => (
            <div key={a.id} className="rounded-xl border border-slate-700 bg-[#151F30] p-5" data-testid={`album-admin-${a.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><div className="font-heading text-lg font-bold uppercase text-white">{a.title}</div><div className="text-xs text-slate-400">{a.category} · {(a.photos || []).length} photos · <span className={a.published ? "text-green-400" : "text-amber-400"}>{a.published ? "Published" : "Unpublished"}</span></div></div>
                <div className="flex gap-2">
                  <button onClick={() => togglePublish(a)} className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-bold hover:bg-[#1E63D6]" data-testid={`album-publish-${a.id}`}>{a.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                  <label className="cursor-pointer rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-bold hover:bg-slate-600" data-testid={`album-upload-${a.id}`}>
                    {uploadingTo === a.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                    <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => addPhotos(a.id, Array.from(e.target.files))} />
                  </label>
                  <button onClick={() => setEditing(a)} className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-bold hover:bg-slate-600">Edit</button>
                  <button onClick={() => removeAlbum(a.id)} className="rounded-lg bg-slate-700 px-3 py-1.5 text-xs font-bold hover:bg-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
              {(a.photos || []).length > 0 && (
                <div className="mt-4 grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-8">
                  {a.photos.map((p) => (
                    <div key={p.id} className="group relative aspect-square overflow-hidden rounded-lg bg-slate-700">
                      <img src={mediaUrl(p.url)} alt="" className="h-full w-full object-cover" />
                      <div className="absolute inset-0 hidden items-center justify-center gap-1 bg-black/60 group-hover:flex">
                        <button onClick={() => setCover(a, p.url)} className="rounded bg-white/20 px-1.5 py-1 text-[10px] font-bold text-white">Cover</button>
                        <button onClick={() => deletePhoto(p.id)} className="rounded bg-red-600 p-1 text-white"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
          {albums.length === 0 && <p className="py-10 text-center text-slate-500">No albums yet.</p>}
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg border-slate-700 bg-[#151F30] text-white">
          <DialogHeader><DialogTitle className="font-heading uppercase">{editing?.id ? "Edit" : "New"} Album</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid gap-4">
              <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Title</label><input className={inputCls} value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="album-title" /></div>
              <Field field={{ key: "category", label: "Category", type: "select", options: CATEGORIES.map((c) => ({ value: c, label: c })) }} value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} />
              <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Description</label><textarea rows={2} className={inputCls} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <Field field={{ key: "published", label: "Published", type: "switch" }} value={editing.published} onChange={(v) => setEditing({ ...editing, published: v })} />
              <label className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
                <input type="checkbox" checked={!!editing.consent_confirmed} onChange={(e) => setEditing({ ...editing, consent_confirmed: e.target.checked })} data-testid="album-consent" className="mt-1" />
                <span>I confirm appropriate consent has been obtained to publish these photographs (POPIA / child protection).</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEditing(null)} className="rounded-lg border border-slate-600 px-4 py-2 text-sm font-semibold text-slate-300">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="album-save-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-5 py-2 text-sm font-bold uppercase text-[#0A1626]">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
